import type { StaffPrivacyRequestView } from "@lafayette/schemas";
import { useQueryClient } from "@tanstack/react-query";
import { createColumnHelper } from "@tanstack/react-table";
import { type ReactNode, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { deepLinks } from "../../navigation/deep-links";
import { areaPath } from "../../navigation/offering";
import { useAreaHref } from "../../navigation/use-offering";
import { AsyncState } from "../../ui/async-state";
import { Button } from "../../ui/button";
import { Card, CardTitle } from "../../ui/card";
import { DataTable } from "../../ui/data-table";
import { SelectField, TextField } from "../../ui/fields";
import { Icon } from "../../ui/icon";
import { BackLink } from "../../ui/links";
import { PageHeader } from "../../ui/page-header";
import { StatusBadge, type StatusTone } from "../../ui/status-badge";
import { StatusMessage } from "../../ui/status-message";
import { type PrivacyCapabilities, usePrivacyCapabilities } from "../access";
import { PrivacyConfirmDialog } from "../components/PrivacyConfirmDialog";
import { describePrivacyError } from "../errors";
import {
  errorCodeLabels,
  kindLabels,
  reasonLabels,
  stateGlyphs,
  stateLabels,
  stepLabels,
  stepSummary,
} from "../labels";
import { privacyKeys, usePrivacyMatrix, usePrivacyRequests } from "../queries";
import { requestActionsFor } from "../request-actions";
import { usePrivacyRuntime } from "../runtime";

// The privacy area of Customers (CHT-771; supervisors and administrators). Export and deletion requests of customers:
//   - ask on behalf of a customer (the confirm dialog shows exactly what happens);
//   - approve or reject a request that another person made: the person who asked cannot decide their own request;
//   - see the step ledger of a running deletion: which step is done, which failed and why, how many times it was tried;
//   - run a partly done request again, withdraw a request nobody has started, download the export that staff asked for.
// Every command is audited by the server. This page shows ids, closed codes and times: no customer data.
//
// Every button of a request has its own name: its visible words, then a hidden tail with the kind of the request and the first eight characters
// of its id ("Approve… deletion request d1000001"), so a list of the controls of the page has no repeated name (WALK-3). The tail holds no customer text.

type Request = StaffPrivacyRequestView;
type DialogState =
  | Readonly<{ kind: "create" }>
  | Readonly<{ kind: "decide"; request: Request; decision: "APPROVE" | "REJECT" }>
  | Readonly<{ kind: "cancel"; request: Request }>
  | Readonly<{ kind: "retry"; request: Request }>
  | null;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const utc = (iso: string) => `${iso.slice(0, 10)} ${iso.slice(11, 16)} UTC`;

const stateTones: Readonly<Record<Request["state"], StatusTone>> = {
  REQUESTED: "neutral",
  APPROVED: "info",
  EXECUTING: "info",
  COMPLETED: "success",
  PARTIALLY_COMPLETED: "warning",
  REJECTED: "danger",
  CANCELLED: "neutral",
};

/** The state of a request: its own icon and its words (the words may be long, so the badge may wrap). */
function StateBadge({ state }: Readonly<{ state: Request["state"] }>) {
  return (
    <StatusBadge
      tone={stateTones[state]}
      glyph={<Icon name={stateGlyphs[state]} />}
      className="whitespace-normal [&_svg]:self-start"
    >
      {stateLabels[state]}
    </StatusBadge>
  );
}

type Step = Request["steps"][number];

// The column list is a module constant: `DataTable` draws a cell with `flexRender`, which treats a cell function as a component.
const stepColumn = createColumnHelper<Step>();
const stepColumns = [
  stepColumn.display({
    id: "step",
    header: "Step",
    meta: {
      rowHeader: true,
      headClassName: "first:pl-0 @max-[720px]/dt:first:pl-0",
      className: "first:pl-0 @max-[720px]/dt:first:pl-0",
    },
    cell: ({ row }) => stepLabels[row.original.step],
  }),
  stepColumn.display({
    id: "state",
    header: "State",
    cell: ({ row }) =>
      row.original.state === "DONE" ? (
        <StatusBadge tone="success">Done</StatusBadge>
      ) : row.original.state === "FAILED" ? (
        <StatusBadge tone="danger">Failed</StatusBadge>
      ) : (
        <StatusBadge tone="neutral" glyph={<Icon name="hourglass" />}>
          Waiting
        </StatusBadge>
      ),
  }),
  stepColumn.display({
    id: "tries",
    header: "Tries",
    meta: { className: "tabular-nums" },
    cell: ({ row }) => row.original.attempts,
  }),
  stepColumn.display({
    id: "changed",
    header: "Changed",
    meta: { className: "tabular-nums" },
    cell: ({ row }) => row.original.affected ?? "–",
  }),
  stepColumn.display({
    id: "note",
    header: "Note",
    meta: {
      headClassName: "last:pr-0 @max-[720px]/dt:last:pr-0",
      className: "last:pr-0 @max-[720px]/dt:last:pr-0",
    },
    cell: ({ row }) =>
      row.original.lastErrorCode === null ? "" : errorCodeLabels[row.original.lastErrorCode],
  }),
];

function StepLedger({ request }: Readonly<{ request: Request }>) {
  if (request.steps.length === 0)
    return (
      <p className="m-0 text-sm text-muted-foreground">
        No steps yet. They are made when the request is approved.
      </p>
    );
  return (
    <DataTable<Step>
      caption="Steps of this request"
      data={request.steps}
      getRowId={(step) => step.step}
      columns={stepColumns}
      stickyHeader={false}
      className="rounded-none border-0"
      rowClassName={() => "h-[38px] border-hairline"}
    />
  );
}

type AskCardProps = Readonly<{
  customerId: string;
  onCustomerId: (value: string) => void;
  valid: boolean;
  kind: "EXPORT" | "DELETE";
  onKind: (value: "EXPORT" | "DELETE") => void;
  reason: "STAFF_ASSISTED" | "LEGAL_REQUEST" | "TEST_CUSTOMER";
  onReason: (value: "STAFF_ASSISTED" | "LEGAL_REQUEST" | "TEST_CUSTOMER") => void;
  onSubmit: () => void;
}>;

/** The form that asks on behalf of a customer. The confirm dialog (opened by `onSubmit`) shows exactly what happens. */
function AskCard({
  customerId,
  onCustomerId,
  valid,
  kind,
  onKind,
  reason,
  onReason,
  onSubmit,
}: AskCardProps) {
  return (
    <section aria-labelledby="ask-title">
      <Card className="grid gap-4 p-5">
        <CardTitle id="ask-title">Ask on behalf of a customer</CardTitle>
        <form
          className="grid max-w-3xl gap-4 md:grid-cols-2"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            if (valid) onSubmit();
          }}
        >
          <div className="md:col-span-2">
            <TextField
              id="privacy-customer-id"
              label="Customer id"
              hint="Copy it from the customer page. It is not a name."
              value={customerId}
              onChange={onCustomerId}
              required
              error={customerId !== "" && !valid ? "This is not a customer id." : undefined}
            />
          </div>
          <SelectField
            id="privacy-kind"
            label="What to ask for"
            value={kind}
            onChange={(value) => onKind(value === "DELETE" ? "DELETE" : "EXPORT")}
            options={[
              { value: "EXPORT", label: kindLabels.EXPORT },
              { value: "DELETE", label: kindLabels.DELETE },
            ]}
          />
          <SelectField
            id="privacy-reason"
            label="Why"
            value={reason}
            onChange={(value) =>
              onReason(
                value === "LEGAL_REQUEST" || value === "TEST_CUSTOMER" ? value : "STAFF_ASSISTED",
              )
            }
            options={(["STAFF_ASSISTED", "LEGAL_REQUEST", "TEST_CUSTOMER"] as const).map(
              (code) => ({
                value: code,
                label: reasonLabels[code],
              }),
            )}
          />
          <div className="md:col-span-2">
            <Button type="submit" disabled={!valid}>
              Review and ask
            </Button>
          </div>
        </form>
      </Card>
    </section>
  );
}

/** The term and value pairs of a request. The first column (the customer id) is the widest. */
function RequestFacts({
  request,
  customerHref,
}: Readonly<{ request: Request; customerHref: string | null }>) {
  const customer =
    customerHref === null ? (
      <span>{request.customerId}</span>
    ) : (
      <Link className="text-link underline underline-offset-4 hover:no-underline" to={customerHref}>
        {request.customerId}
      </Link>
    );
  const pairs: readonly (readonly [string, ReactNode])[] = [
    ["Customer", customer],
    ["Asked by", request.requesterKind === "customer" ? "The customer" : "A staff member"],
    ["Why", reasonLabels[request.reasonCode]],
    ["Asked at", utc(request.createdAt)],
    ["Progress", stepSummary(request.steps)],
  ];
  return (
    <dl className="m-0 grid grid-cols-[repeat(auto-fit,minmax(10.5rem,1fr))] gap-x-4 gap-y-2 xl:grid-cols-[minmax(19rem,2.6fr)_minmax(0,1fr)_minmax(0,1.8fr)_minmax(0,1fr)_minmax(0,1fr)]">
      {pairs.map(([term, value]) => (
        <div key={term} className="grid min-w-0 content-start gap-0.5">
          <dt className="text-xs font-semibold text-muted-foreground">{term}</dt>
          <dd className="m-0 text-sm [overflow-wrap:anywhere]">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

type RequestCardProps = Readonly<{
  request: Request;
  capabilities: PrivacyCapabilities;
  customerHref: string | null;
  open: boolean;
  onToggle: () => void;
  onDialog: (dialog: DialogState) => void;
  onDownload: () => void;
}>;

/**
 * The tail of every button name: which request this is (its kind and the first eight characters of its id: an id is not text a person
 * typed, and unlike an ordinal it does not move when the list is read again), never the customer.
 */
function WhichRequest({ request }: Readonly<{ request: Request }>) {
  return (
    <span className="sr-only">
      {request.kind === "DELETE" ? "deletion" : "export"} request {request.id.slice(0, 8)}
    </span>
  );
}

/** A quiet outline button of a request card. Its name is its words, then which request it acts on. */
function RequestButton({
  request,
  onClick,
  expanded,
  variant = "outline",
  children,
}: Readonly<{
  request: Request;
  onClick: () => void;
  expanded?: boolean;
  variant?: "outline" | "danger" | "default";
  children: ReactNode;
}>) {
  return (
    <Button type="button" size="sm" variant={variant} aria-expanded={expanded} onClick={onClick}>
      {children} <WhichRequest request={request} />
    </Button>
  );
}

/** Approve and reject. Approving a deletion is the button that cannot be undone, so it is the red one. */
function DecideButtons({
  request,
  onDialog,
}: Readonly<{ request: Request; onDialog: (dialog: DialogState) => void }>) {
  return (
    <>
      <RequestButton
        request={request}
        variant={request.kind === "DELETE" ? "danger" : "default"}
        onClick={() => onDialog({ kind: "decide", request, decision: "APPROVE" })}
      >
        Approve…
      </RequestButton>
      <RequestButton
        request={request}
        onClick={() => onDialog({ kind: "decide", request, decision: "REJECT" })}
      >
        Reject…
      </RequestButton>
    </>
  );
}

/** The buttons of one request that this role may use (`requestActionsFor` decides which). */
function RequestButtons({
  request,
  capabilities,
  open,
  onToggle,
  onDialog,
  onDownload,
}: Omit<RequestCardProps, "customerHref">) {
  const actions = requestActionsFor(request, capabilities);
  return (
    <div className="flex flex-wrap gap-2">
      <RequestButton request={request} expanded={open} onClick={onToggle}>
        {open ? <Icon name="arrow-up-01-round" /> : <Icon name="arrow-down-01-round" />}
        {open ? "Hide the steps" : "Show the steps"}
      </RequestButton>
      {actions.decide ? <DecideButtons request={request} onDialog={onDialog} /> : null}
      {actions.retry ? (
        <RequestButton request={request} onClick={() => onDialog({ kind: "retry", request })}>
          Run again…
        </RequestButton>
      ) : null}
      {actions.cancel ? (
        <RequestButton request={request} onClick={() => onDialog({ kind: "cancel", request })}>
          Withdraw…
        </RequestButton>
      ) : null}
      {actions.download ? (
        <RequestButton request={request} onClick={onDownload}>
          <Icon name="download-01" />
          Download the export file
        </RequestButton>
      ) : null}
    </div>
  );
}

/** The export file line under a request that made one. */
function ExportLine({ file }: Readonly<{ file: NonNullable<Request["export"]> }>) {
  return (
    <p className="m-0 text-sm text-muted-foreground">
      Export file: {file.available ? "ready" : "not available now"}, {file.byteSize} bytes,{" "}
      {file.downloadsLeft} downloads left, ends {utc(file.expiresAt)}.
    </p>
  );
}

/** One request: what it is, its state, who asked, the buttons that this role may use, the steps and the export file. */
function RequestCard({ request, customerHref, open, ...buttons }: RequestCardProps) {
  return (
    <li className="privacy-item">
      <Card className="grid gap-3.5 p-5">
        <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
          <strong className="text-base leading-6">{kindLabels[request.kind]}</strong>
          <StateBadge state={request.state} />
        </div>
        <RequestFacts request={request} customerHref={customerHref} />
        {request.lastErrorCode === null ? null : (
          <p className="m-0 text-sm font-semibold" role="note">
            Last problem: {errorCodeLabels[request.lastErrorCode]}
          </p>
        )}
        <RequestButtons request={request} open={open} {...buttons} />
        {open ? <StepLedger request={request} /> : null}
        {request.export === null ? null : <ExportLine file={request.export} />}
      </Card>
    </li>
  );
}

export function PrivacyPage() {
  const { capabilities, isLoading: rolesLoading } = usePrivacyCapabilities();
  // An administrator holds the Privacy area but not the Customers area, so a link to a customer is plain text for them.
  const areaHref = useAreaHref();
  const customerSupportHref = (id: string) => areaHref("customers", deepLinks.customerSupport(id));
  const runtime = usePrivacyRuntime();
  const client = useQueryClient();
  const [params] = useSearchParams();
  const customerFilter = params.get("customerId");
  const filter = {
    ...(customerFilter !== null && UUID.test(customerFilter)
      ? { customerId: customerFilter.toLowerCase() }
      : {}),
  };
  const [refreshToken, setRefreshToken] = useState(0);
  const requests = usePrivacyRequests(filter, capabilities.read, refreshToken);
  const matrix = usePrivacyMatrix(capabilities.read);
  const [dialog, setDialog] = useState<DialogState>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [customerId, setCustomerId] = useState(filter.customerId ?? "");
  const [kind, setKind] = useState<"EXPORT" | "DELETE">("EXPORT");
  const [reason, setReason] = useState<"STAFF_ASSISTED" | "LEGAL_REQUEST" | "TEST_CUSTOMER">(
    "STAFF_ASSISTED",
  );

  if (rolesLoading) {
    return (
      <AsyncState kind="loading" title="Checking your access" message="Reading your staff role." />
    );
  }
  if (!capabilities.read) {
    return (
      <AsyncState
        kind="error"
        title="Not available for your role"
        message="Only a supervisor or an administrator works with privacy requests."
      />
    );
  }

  const reload = () => {
    setRefreshToken((token) => token + 1);
    void client.invalidateQueries({ queryKey: privacyKeys.all });
  };
  const finish = (message: string) => {
    setDialog(null);
    setNotice(message);
    setProblem(null);
    reload();
  };

  async function download(request: Request) {
    setProblem(null);
    try {
      runtime.saveFile(await runtime.api.downloadExport(request.id));
      setNotice("The export file was downloaded. This was written in the audit trail.");
      reload();
    } catch (error) {
      setNotice(null);
      setProblem(describePrivacyError(error).message);
      reload();
    }
  }

  const items = requests.data?.items ?? [];
  const customerIdValid = UUID.test(customerId.trim());

  return (
    <section className="grid grid-cols-[minmax(0,1fr)] gap-6" aria-labelledby="page-title">
      <PageHeader
        eyebrow="Lafayette operations"
        title="Privacy requests"
        titleId="page-title"
        description="Export and deletion requests of customers. A deletion removes or anonymizes a customer's data and cannot be undone. A request that staff make must be approved by another supervisor or administrator. Every step is recorded."
        breadcrumbs={
          areaHref("customers", areaPath("customers")) === null ? undefined : (
            <BackLink to={areaPath("customers")}>Back to customers</BackLink>
          )
        }
        actions={
          customerFilter === null ? null : (
            <Button asChild variant="outline" size="sm">
              <Link to={areaPath("privacy")}>Show every customer</Link>
            </Button>
          )
        }
      />

      {notice === null ? null : <StatusMessage message={{ tone: "success", text: notice }} />}
      {problem === null ? null : <StatusMessage message={{ tone: "error", text: problem }} />}

      {capabilities.request ? (
        <AskCard
          customerId={customerId}
          onCustomerId={setCustomerId}
          valid={customerIdValid}
          kind={kind}
          onKind={setKind}
          reason={reason}
          onReason={setReason}
          onSubmit={() => setDialog({ kind: "create" })}
        />
      ) : null}

      <section className="grid gap-3" aria-labelledby="requests-title">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 id="requests-title" className="m-0 font-serif text-[28px] leading-[34px] font-medium">
            Requests
          </h2>
          <Button type="button" variant="outline" size="sm" onClick={reload}>
            <Icon name="refresh" />
            Refresh
          </Button>
        </div>
        <p className="m-0 text-sm leading-6 text-muted-foreground">
          A request that is approved or running is read again every few seconds for two minutes.
          Press Refresh to read it now and to start this again.
        </p>
        {requests.isPending ? (
          <AsyncState
            kind="loading"
            title="Loading the requests"
            message="Waiting for the answer."
          />
        ) : requests.isError ? (
          <AsyncState
            kind="error"
            title="The requests could not be loaded"
            message={describePrivacyError(requests.error).message}
            onRetry={() => void requests.refetch()}
          />
        ) : items.length === 0 ? (
          <AsyncState
            kind="empty"
            title="No privacy request"
            message="No customer has asked, and nobody has asked for a customer."
          />
        ) : (
          <ul className="m-0 grid list-none gap-4 p-0" aria-label="Privacy requests, newest first">
            {items.map((request) => (
              <RequestCard
                key={request.id}
                request={request}
                capabilities={capabilities}
                customerHref={customerSupportHref(request.customerId)}
                open={expanded === request.id}
                onToggle={() => setExpanded(expanded === request.id ? null : request.id)}
                onDialog={setDialog}
                onDownload={() => void download(request)}
              />
            ))}
          </ul>
        )}
      </section>

      {dialog?.kind === "create" ? (
        <PrivacyConfirmDialog
          title={
            kind === "DELETE"
              ? "Ask for the deletion of this customer"
              : "Ask for an export of this customer's data"
          }
          confirmLabel={kind === "DELETE" ? "Ask for the deletion" : "Ask for the export"}
          tone={kind === "DELETE" ? "danger" : "primary"}
          subject={kind}
          summary={
            kind === "DELETE"
              ? "This only asks. Nothing is deleted until another supervisor or administrator approves it."
              : "This only asks. Another supervisor or administrator must approve it before the file is made."
          }
          matrix={matrix.data}
          send={() =>
            runtime.api.createRequest({
              customerId: customerId.trim().toLowerCase(),
              kind,
              reasonCode: reason,
            })
          }
          onDone={() =>
            finish("The request was made. Another supervisor or administrator must decide it.")
          }
          onReload={reload}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === "decide" ? (
        <PrivacyConfirmDialog
          title={
            dialog.decision === "APPROVE"
              ? dialog.request.kind === "DELETE"
                ? "Approve the deletion"
                : "Approve the export"
              : "Reject the request"
          }
          confirmLabel={dialog.decision === "APPROVE" ? "Approve" : "Reject"}
          tone={
            dialog.decision === "APPROVE" && dialog.request.kind === "DELETE" ? "danger" : "primary"
          }
          subject={dialog.decision === "APPROVE" ? dialog.request.kind : "OTHER"}
          summary={
            dialog.decision === "APPROVE"
              ? dialog.request.kind === "DELETE"
                ? "After you approve, the deletion starts at once. Read what it does."
                : "After you approve, the export file is made at once."
              : "The request ends. Nothing is deleted or made. The reason of the request stays in the audit trail."
          }
          matrix={matrix.data}
          send={() =>
            runtime.api.decide(dialog.request.id, {
              expectedVersion: dialog.request.version,
              decision: dialog.decision,
            })
          }
          onDone={() =>
            finish(
              dialog.decision === "APPROVE"
                ? "The request was approved."
                : "The request was rejected.",
            )
          }
          onReload={reload}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === "cancel" ? (
        <PrivacyConfirmDialog
          title="Withdraw the request"
          confirmLabel="Withdraw"
          subject="OTHER"
          summary="The request ends. Nothing is deleted or made. This only works while nobody has started it."
          send={() =>
            runtime.api.cancelRequest(dialog.request.id, {
              expectedVersion: dialog.request.version,
            })
          }
          onDone={() => finish("The request was withdrawn.")}
          onReload={reload}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === "retry" ? (
        <PrivacyConfirmDialog
          title="Run the request again"
          confirmLabel="Run again"
          subject="OTHER"
          summary="Only the steps that are not done are tried again. A step that is done is never done twice."
          send={() =>
            runtime.api.retryRequest(dialog.request.id, { expectedVersion: dialog.request.version })
          }
          onDone={() => finish("The request will run again.")}
          onReload={reload}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </section>
  );
}
